import { Injectable } from '@angular/core';
import { AppSettingsService } from './app-settings.service';
import { map, shareReplay } from 'rxjs/operators';
import { HttpClient } from '@angular/common/http';
import { Observable, from, firstValueFrom, Subject } from 'rxjs';
import { PrioritizesScanningBarcodeEnum, SOLockAutomaticActions, SOLockSettings, SOLockTypes, SystemSetting, SystemSettingsEnum, UiControlToFocusOnAfterAddingItemEnum } from 'src/app/shared/models/system-settings';
import { DexieDbProvider } from '@shared';
import { AuthService } from '@auth';
import { DataSourceService } from './data-source.service';
import moment from 'moment';
import { QueryStringHelper } from 'src/app/shared/helpers/query-string.helper';
import { PagedList } from 'src/app/shared/models/paged-list';
import { InventoryManagementMethod } from 'src/app/shared/models/Inventory-management-method';
import { OrganizationProfile, OrdersSetting, PurchaseInvoicesSetting, SalesInvoicesSetting, ProductsSetting, GlobalSettings, ModulesActivation, EInvoiceKsaConfiguration, SubscriptionsSetting, SubscriptionsChurnReasons, SubscriptionsFxRates, SubscriptionsCurrenciesUsed, SubscriptionsOpeningBalances, SubscriptionsCustomerFields, SubscriptionsProductFields, SubscriptionsSubscriptionFields, SubscriptionWorkflow, cloneSubscriptionWorkflows } from 'src/app/system/models/system-settings';


@Injectable({
    providedIn: 'root'
})
export class SystemSettingsService {

    baseUrl = '';
    modulesActivationSettingsChanged$: Subject<ModulesActivation> = new Subject();

    private offlineModeEnabled$?: Observable<boolean>;
    private offlineModeExcludedUsers$?: Observable<string>;
    private hideSubTotal$?: Observable<boolean>;
    private systemStartDate$?: Observable<string>;
    private financialYearEndDate$?: Observable<string>;
    private allowSellingBySerialNumber$?: Observable<boolean>;
    private checkSerialOwner$?: Observable<boolean>;
    private checkSerialAvailability$?: Observable<boolean>;
    private checkSerialFulfillment$?: Observable<boolean>;
    private inventoryManagementMethod$?: Observable<InventoryManagementMethod>;
    private hideBalanceAndBatchDetails$?: Observable<boolean>;
    private uiControlToFocusOnAfterAddingItem$?: Observable<string>;
    private prioritizesScanningBarcode$?: Observable<PrioritizesScanningBarcodeEnum>;
    private preventSellingZeroBalanceStockItems$?: Observable<boolean>;
    private hideCostOnProductionOrderPrint$?: Observable<boolean>;
    private orderTakerUsers$?: Observable<string>;
    private order3OfflineModeEnabled$?: Observable<boolean>;
    private useCashOnDelivery$?: Observable<boolean>;
    private realTimeStockItemBalanceUpdate$?: Observable<boolean>;
    private soLockSettings$?: Observable<SOLockSettings>;


    private isEdara2IntegrationConnected$?: Observable<boolean>;
    private edaraSetupCompleted$?: Observable<boolean>;
    private globalSettings$?: Observable<GlobalSettings>;
    private organizationProfile$?: Observable<OrganizationProfile>;
    private ordersSettings$?: Observable<OrdersSetting>;
    private purchaseInvoicesSettings$?: Observable<PurchaseInvoicesSetting>;
    private salesInvoicesSettings$?: Observable<SalesInvoicesSetting>;
    private productsSettings$?: Observable<ProductsSetting>;
    private subscriptionsSettings$?: Observable<SubscriptionsSetting>;
    private subscriptionsChurnReasons$?: Observable<SubscriptionsChurnReasons>;
    private subscriptionsFxRates$?: Observable<SubscriptionsFxRates>;
    private subscriptionsCurrenciesUsed$?: Observable<SubscriptionsCurrenciesUsed>;
    private subscriptionsOpeningBalances$?: Observable<SubscriptionsOpeningBalances>;
    private subscriptionsCustomerFields$?: Observable<SubscriptionsCustomerFields>;
    private subscriptionsProductFields$?: Observable<SubscriptionsProductFields>;
    private subscriptionsSubscriptionFields$?: Observable<SubscriptionsSubscriptionFields>;
    private subscriptionsWorkflows$?: Observable<SubscriptionWorkflow[]>;

    constructor(
        private httpClient: HttpClient,
        private authService: AuthService,
        private dataSourceService: DataSourceService,
        private localDbProvider: DexieDbProvider) {
        this.baseUrl = AppSettingsService.appSettings.edaraCoreApi.baseUrl;
        this.authService.tenantSwitched$.subscribe({
            next: () => this.clearCache()
        });
    }

    public async offlineModeEnabled(): Promise<boolean> {
        if (!this.offlineModeEnabled$) {
            this.offlineModeEnabled$ = from(this.getSettingValue(SystemSettingsEnum.OfflineModeEnabled)).pipe(
                map(res => res ? (res.value === '1' || res.value === 'true') : false),
                shareReplay(1)
            );
        }
        return firstValueFrom(this.offlineModeEnabled$);
    }

    public async offlineModeExcludedUsers(): Promise<string> {
        if (!this.offlineModeExcludedUsers$) {
            this.offlineModeExcludedUsers$ = from(this.getSettingValue(SystemSettingsEnum.OfflineModeExcludedUsers)).pipe(
                map(res => res ? res.value : ''),
                shareReplay(1)
            );
        }
        return firstValueFrom(this.offlineModeExcludedUsers$);
    }

    /**
     * True when the organization's "Save order offline first" setting is on and the
     * user is not on its excluded-users list — the v2.5 offline-first decision.
     */
    public async isOfflineFirstUser(userEmail: string): Promise<boolean> {
        if (!await this.offlineModeEnabled()) return false;
        const excludedUsers = await this.offlineModeExcludedUsers();
        return !(excludedUsers ?? '').split(',').includes(userEmail);
    }

    public async hideSubTotal(): Promise<boolean> {
        if (!this.hideSubTotal$) {
            this.hideSubTotal$ = from(this.getSettingValue(SystemSettingsEnum.HideSubTotal)).pipe(
                map(res => res ? (res.value === '1' || res.value === 'true') : false),
                shareReplay(1)
            );
        }
        return firstValueFrom(this.hideSubTotal$);
    }

    public async systemStartDate(): Promise<string> {
        if (!this.systemStartDate$) {
            this.systemStartDate$ = from(this.getSettingValue(SystemSettingsEnum.SystemStartDate)).pipe(
                map(res => res ? moment(res.value, 'DD/MM/YYYY').toISOString() : ''),
                shareReplay(1)
            );
        }
        return firstValueFrom(this.systemStartDate$);
    }

    public async financialYearEndDate(): Promise<string> {
        if (!this.financialYearEndDate$) {
            this.financialYearEndDate$ = from(this.getSettingValue(SystemSettingsEnum.FinancialYearEndDate)).pipe(
                map(res => res ? res.value : ''),
                shareReplay(1)
            );
        }
        return firstValueFrom(this.financialYearEndDate$);
    }

    public async allowSellingBySerialNumber(): Promise<boolean> {
        if (!this.allowSellingBySerialNumber$) {
            this.allowSellingBySerialNumber$ = from(this.getSettingValue(SystemSettingsEnum.AllowSellingBySerialNumber)).pipe(
                map(res => res ? (res.value === '1' || res.value === 'true') : false),
                shareReplay(1)
            );
        }
        return firstValueFrom(this.allowSellingBySerialNumber$);
    }

    public async checkSerialOwner(): Promise<boolean> {
        if (!this.checkSerialOwner$) {
            this.checkSerialOwner$ = from(this.getSettingValue(SystemSettingsEnum.CheckSerialOwner)).pipe(
                map(res => res ? (res.value === '1' || res.value === 'true') : false),
                shareReplay(1)
            );
        }
        return firstValueFrom(this.checkSerialOwner$);
    }

    public async checkSerialAvailability(): Promise<boolean> {
        if (!this.checkSerialAvailability$) {
            this.checkSerialAvailability$ = from(this.getSettingValue(SystemSettingsEnum.CheckSerialAvailability)).pipe(
                map(res => res ? (res.value === '1' || res.value === 'true') : false),
                shareReplay(1)
            );
        }
        return firstValueFrom(this.checkSerialAvailability$);
    }

    public async checkSerialFulfillment(): Promise<boolean> {
        if (!this.checkSerialFulfillment$) {
            this.checkSerialFulfillment$ = from(this.getSettingValue(SystemSettingsEnum.CheckSerialFulfillment)).pipe(
                map(res => res ? (res.value === '1' || res.value === 'true') : false),
                shareReplay(1)
            );
        }
        return firstValueFrom(this.checkSerialFulfillment$);
    }

    public async inventoryManagementMethod(): Promise<InventoryManagementMethod> {
        if (!this.inventoryManagementMethod$) {
            this.inventoryManagementMethod$ = from(this.getSettingValue(SystemSettingsEnum.InventoryManagementMethod)).pipe(
                map(res => res ? res.value as InventoryManagementMethod : InventoryManagementMethod.None),
                shareReplay(1)
            );
        }
        return firstValueFrom(this.inventoryManagementMethod$);
    }

    public async hideBalanceAndBatchDetails(): Promise<boolean> {
        if (!this.hideBalanceAndBatchDetails$) {
            this.hideBalanceAndBatchDetails$ = from(this.getSettingValue(SystemSettingsEnum.HideBalanceAndBatchDetails)).pipe(
                map(res => res ? (res.value === '1' || res.value === 'true') : false),
                shareReplay(1)
            );
        }
        return firstValueFrom(this.hideBalanceAndBatchDetails$);
    }

    public async uiControlToFocusOnAfterAddingItem(): Promise<string> {
        if (!this.uiControlToFocusOnAfterAddingItem$) {
            this.uiControlToFocusOnAfterAddingItem$ = from(this.getSettingValue(SystemSettingsEnum.UiControlToFocusOnAfterAddingItem)).pipe(
                map(res => res ? res.value : UiControlToFocusOnAfterAddingItemEnum.SearchInput),
                shareReplay(1)
            );
        }
        return firstValueFrom(this.uiControlToFocusOnAfterAddingItem$);
    }

    public async prioritizesScanningBarcode(): Promise<PrioritizesScanningBarcodeEnum> {
        if (!this.prioritizesScanningBarcode$) {
            this.prioritizesScanningBarcode$ = from(this.getSettingValue(SystemSettingsEnum.PrioritizesScanningBarcode)).pipe(
                map(res => res ? +res.value : PrioritizesScanningBarcodeEnum.normalOverWeighted),
                shareReplay(1)
            );
        }
        return firstValueFrom(this.prioritizesScanningBarcode$);
    }

    public async preventSellingZeroBalanceStockItems(): Promise<boolean> {
        if (!this.preventSellingZeroBalanceStockItems$) {
            this.preventSellingZeroBalanceStockItems$ = from(this.getSettingValue(SystemSettingsEnum.PreventSellingZeroBalanceStockItems))
                .pipe(
                    map(res => res ? (res.value === '1' || res.value === 'true') : false),
                    shareReplay(1)
                );
        }
        return firstValueFrom(this.preventSellingZeroBalanceStockItems$);
    }

    public async hideCostOnProductionOrderPrint(): Promise<boolean> {
        if (!this.hideCostOnProductionOrderPrint$) {
            this.hideCostOnProductionOrderPrint$ = from(this.getSettingValue(SystemSettingsEnum.HideCostOnProductionOrderPrint))
                .pipe(
                    map(res => res ? (res.value === '1' || res.value === 'true') : false),
                    shareReplay(1)
                );
        }
        return firstValueFrom(this.hideCostOnProductionOrderPrint$);
    }

    public async orderTakerUsers(): Promise<string> {
        if (!this.orderTakerUsers$) {
            this.orderTakerUsers$ = from(this.getSettingValue(SystemSettingsEnum.OrderTakerUsers)).pipe(
                map(res => res ? res.value : ''),
                shareReplay(1)
            );
        }
        return firstValueFrom(this.orderTakerUsers$);
    }

    public async order3OfflineModeEnabled(): Promise<boolean> {
        if (!this.order3OfflineModeEnabled$) {
            this.order3OfflineModeEnabled$ = from(new Promise<boolean>((resolve) => resolve(true)));
        }
        return firstValueFrom(this.order3OfflineModeEnabled$);
    }

    public async useCashOnDelivery(): Promise<boolean> {
        if (!this.useCashOnDelivery$) {
            this.useCashOnDelivery$ = from(this.getSettingValue(SystemSettingsEnum.UseCashOnDelivery)).pipe(
                map(res => res ? (res.value === '1' || res.value === 'true') : false),
                shareReplay(1)
            );
        }
        return firstValueFrom(this.useCashOnDelivery$);
    }

    public async getSOLockSettings(): Promise<SOLockSettings> {
        if (!this.soLockSettings$) {
            this.soLockSettings$ = from(this.getSettingValue(SystemSettingsEnum.SOLockSettings)).pipe(
                map(res => {
                    if (!res || !res.value) {
                        return {
                            isLockEnabled: false,
                            lockType: SOLockTypes.NoAuto,
                            printTemplates: [],
                            automaticActions: SOLockAutomaticActions.NoAutomaticActions
                        } as SOLockSettings;
                    }

                    const settings = JSON.parse(res.value);
                    return {
                        isLockEnabled: settings.isLockEnabled ?? false,
                        lockType: settings.lockType ?? SOLockTypes.NoAuto,
                        printTemplates: Array.isArray(settings.printTemplates) ? settings.printTemplates : [],
                        automaticActions: settings.automaticActions ?? SOLockAutomaticActions.NoAutomaticActions
                    };
                }),
                shareReplay(1)
            );
        }
        return firstValueFrom(this.soLockSettings$);
    }


    public async realTimeStockItemBalanceUpdate(): Promise<boolean> {
        if (!this.realTimeStockItemBalanceUpdate$) {
            this.realTimeStockItemBalanceUpdate$ = from(this.getSettingValue(SystemSettingsEnum.RealTimeStockItemBalanceUpdate)).pipe(
                map(res => res ? (res.value === '1' || res.value === 'true') : false),
                shareReplay(1)
            );
        }
        return firstValueFrom(this.realTimeStockItemBalanceUpdate$);
    }

    public async defaultCurrency(): Promise<number> {
        if (!this.organizationProfile$) {
            this.organizationProfile$ = from(this.getSettingValue(SystemSettingsEnum.OrganizationProfile)).pipe(
                map(res => res ? OrganizationProfile.clone(JSON.parse(res.value)) : new OrganizationProfile()),
                shareReplay(1)
            );
        }
        return (await firstValueFrom(this.organizationProfile$)).currency;
    }

    public async IsEdara2IntegrationConnected(): Promise<boolean> {
        if (!this.isEdara2IntegrationConnected$) {
            this.isEdara2IntegrationConnected$ = from(this.getSettingValue(SystemSettingsEnum.IsEdara2IntegrationConnected)).pipe(
                map(res => res ? (res.value === '1' || res.value === 'true') : false),
                shareReplay(1)
            );
        }
        return firstValueFrom(this.isEdara2IntegrationConnected$);
    }

    public async EdaraSetupCompleted(): Promise<boolean> {
        if (!this.edaraSetupCompleted$) {
            this.edaraSetupCompleted$ = from(this.getSettingValue(SystemSettingsEnum.EdaraSetupCompleted)).pipe(
                map(res => res ? (res.value === '1' || res.value === 'true') : false),
                shareReplay(1)
            );
        }
        return firstValueFrom(this.edaraSetupCompleted$);
    }

    public async GlobalSettings(): Promise<GlobalSettings> {
        if (!this.globalSettings$) {
            this.globalSettings$ = from(this.getSettingValue(SystemSettingsEnum.Global)).pipe(
                map(res => res ? GlobalSettings.clone(JSON.parse(res.value)) : new GlobalSettings()),
                shareReplay(1)
            );
        }
        return firstValueFrom(this.globalSettings$);
    }

    public async isDataReplicationEnabled(): Promise<boolean> {
        try {
            const settings = await this.GlobalSettings();
            return settings?.desktopApp?.dataReplication === true;
        } catch (error) {
            console.warn('[SystemSettingsService] Could not read the Data replication setting; treating it as Off.', error);
            return false;
        }
    }

    public async OrganizationProfile(): Promise<OrganizationProfile> {
        if (!this.organizationProfile$) {
            this.organizationProfile$ = from(this.getSettingValue(SystemSettingsEnum.OrganizationProfile)).pipe(
                map(res => res ? OrganizationProfile.clone(JSON.parse(res.value)) : new OrganizationProfile()),
                shareReplay(1)
            );
        }
        return firstValueFrom(this.organizationProfile$);
    }

    public async OrdersSettings(): Promise<OrdersSetting> {
        if (!this.ordersSettings$) {
            this.ordersSettings$ = from(this.getSettingValue(SystemSettingsEnum.Order)).pipe(
                map(res => res ? OrdersSetting.clone(JSON.parse(res.value)) : new OrdersSetting()),
                shareReplay(1)
            );
        }
        return firstValueFrom(this.ordersSettings$);
    }

    public async PurchaseInvoicesSettings(): Promise<PurchaseInvoicesSetting> {
        if (!this.purchaseInvoicesSettings$) {
            this.purchaseInvoicesSettings$ = from(this.getSettingValue(SystemSettingsEnum.PurchaseInvoice)).pipe(
                map(res => res ? PurchaseInvoicesSetting.clone(JSON.parse(res.value)) : new PurchaseInvoicesSetting()),
                shareReplay(1)
            );
        }
        return firstValueFrom(this.purchaseInvoicesSettings$);
    }

    public async SalesInvoicesSettings(): Promise<SalesInvoicesSetting> {
        if (!this.salesInvoicesSettings$) {
            this.salesInvoicesSettings$ = from(this.getSettingValue(SystemSettingsEnum.SalesInvoice)).pipe(
                map(res => res ? SalesInvoicesSetting.clone(JSON.parse(res.value)) : new SalesInvoicesSetting()),
                shareReplay(1)
            );
        }
        return firstValueFrom(this.salesInvoicesSettings$);
    }

    public async ProductsSettings(): Promise<ProductsSetting> {
        if (!this.productsSettings$) {
            this.productsSettings$ = from(this.getSettingValue(SystemSettingsEnum.Product)).pipe(
                map(res => res ? ProductsSetting.clone(JSON.parse(res.value)) : new ProductsSetting()),
                shareReplay(1)
            );
        }
        return firstValueFrom(this.productsSettings$);
    }

    public async SubscriptionsSettings(): Promise<SubscriptionsSetting> {
        if (!this.subscriptionsSettings$) {
            this.subscriptionsSettings$ = from(this.getSettingValue(SystemSettingsEnum.Subscriptions)).pipe(
                map(res => res ? SubscriptionsSetting.clone(JSON.parse(res.value)) : new SubscriptionsSetting()),
                shareReplay(1)
            );
        }
        return firstValueFrom(this.subscriptionsSettings$);
    }

    public async SubscriptionsChurnReasons(): Promise<SubscriptionsChurnReasons> {
        if (!this.subscriptionsChurnReasons$) {
            this.subscriptionsChurnReasons$ = from(this.getSettingValue(SystemSettingsEnum.SubscriptionsChurnReasons)).pipe(
                map(res => res ? SubscriptionsChurnReasons.clone(JSON.parse(res.value)) : new SubscriptionsChurnReasons()),
                shareReplay(1)
            );
        }
        return firstValueFrom(this.subscriptionsChurnReasons$);
    }

    public async SubscriptionsFxRates(): Promise<SubscriptionsFxRates> {
        if (!this.subscriptionsFxRates$) {
            this.subscriptionsFxRates$ = from(this.getSettingValue(SystemSettingsEnum.SubscriptionsFxRates)).pipe(
                map(res => res ? SubscriptionsFxRates.clone(JSON.parse(res.value)) : new SubscriptionsFxRates()),
                shareReplay(1)
            );
        }
        return firstValueFrom(this.subscriptionsFxRates$);
    }

    public async SubscriptionsCurrenciesUsed(): Promise<SubscriptionsCurrenciesUsed> {
        if (!this.subscriptionsCurrenciesUsed$) {
            this.subscriptionsCurrenciesUsed$ = from(this.getSettingValue(SystemSettingsEnum.SubscriptionsCurrenciesUsed)).pipe(
                map(res => res ? SubscriptionsCurrenciesUsed.clone(JSON.parse(res.value)) : new SubscriptionsCurrenciesUsed()),
                shareReplay(1)
            );
        }
        return firstValueFrom(this.subscriptionsCurrenciesUsed$);
    }

    public async SubscriptionsOpeningBalances(): Promise<SubscriptionsOpeningBalances> {
        if (!this.subscriptionsOpeningBalances$) {
            this.subscriptionsOpeningBalances$ = from(this.getSettingValue(SystemSettingsEnum.SubscriptionsOpeningBalances)).pipe(
                map(res => res ? SubscriptionsOpeningBalances.clone(JSON.parse(res.value)) : new SubscriptionsOpeningBalances()),
                shareReplay(1)
            );
        }
        return firstValueFrom(this.subscriptionsOpeningBalances$);
    }

    public async SubscriptionsCustomerFields(): Promise<SubscriptionsCustomerFields> {
        if (!this.subscriptionsCustomerFields$) {
            this.subscriptionsCustomerFields$ = from(this.getSettingValue(SystemSettingsEnum.SubscriptionsCustomerFields)).pipe(
                map(res => res ? SubscriptionsCustomerFields.clone(JSON.parse(res.value)) : new SubscriptionsCustomerFields()),
                shareReplay(1)
            );
        }
        return firstValueFrom(this.subscriptionsCustomerFields$);
    }

    public async SubscriptionsProductFields(): Promise<SubscriptionsProductFields> {
        if (!this.subscriptionsProductFields$) {
            this.subscriptionsProductFields$ = from(this.getSettingValue(SystemSettingsEnum.SubscriptionsProductFields)).pipe(
                map(res => res ? SubscriptionsProductFields.clone(JSON.parse(res.value)) : new SubscriptionsProductFields()),
                shareReplay(1)
            );
        }
        return firstValueFrom(this.subscriptionsProductFields$);
    }

    public async SubscriptionsSubscriptionFields(): Promise<SubscriptionsSubscriptionFields> {
        if (!this.subscriptionsSubscriptionFields$) {
            this.subscriptionsSubscriptionFields$ = from(this.getSettingValue(SystemSettingsEnum.SubscriptionsSubscriptionFields)).pipe(
                map(res => res ? SubscriptionsSubscriptionFields.clone(JSON.parse(res.value)) : new SubscriptionsSubscriptionFields()),
                shareReplay(1)
            );
        }
        return firstValueFrom(this.subscriptionsSubscriptionFields$);
    }

    public async SubscriptionsWorkflows(): Promise<SubscriptionWorkflow[]> {
        if (!this.subscriptionsWorkflows$) {
            this.subscriptionsWorkflows$ = from(this.getSettingValue(SystemSettingsEnum.SubscriptionsWorkflows)).pipe(
                map(res => res ? cloneSubscriptionWorkflows(JSON.parse(res.value)) : []),
                shareReplay(1)
            );
        }
        return firstValueFrom(this.subscriptionsWorkflows$);
    }

    private async getSettingValue(name: string): Promise<SystemSetting | undefined> {
        // US 27702: Dexie holds the PREFERRED tenant's settings; any other tenant must
        // read from the server (the shareReplay caches above are per tenant already —
        // they are cleared on tenantSwitched$).
        const setting = this.dataSourceService.canUseLocalData
            ? await this.getSystemSettingByNameLocalAsync(name)
            : undefined;
        if (setting) {
            return setting;
        } else {
            const response = await this.getSystemSettingByNameAync(name);
            this.saveSystemSettingLocal(response);
            return response;
        }
    }

    clearCache() {
        this.offlineModeEnabled$ = undefined;
        this.offlineModeExcludedUsers$ = undefined;
        this.hideSubTotal$ = undefined;
        this.systemStartDate$ = undefined;
        this.financialYearEndDate$ = undefined;
        this.allowSellingBySerialNumber$ = undefined;
        this.checkSerialOwner$ = undefined;
        this.checkSerialAvailability$ = undefined;
        this.checkSerialFulfillment$ = undefined;
        this.inventoryManagementMethod$ = undefined;
        this.hideBalanceAndBatchDetails$ = undefined;
        this.uiControlToFocusOnAfterAddingItem$ = undefined;
        this.prioritizesScanningBarcode$ = undefined;
        this.preventSellingZeroBalanceStockItems$ = undefined;
        this.hideCostOnProductionOrderPrint$ = undefined;
        this.orderTakerUsers$ = undefined;
        this.order3OfflineModeEnabled$ = undefined;
        this.useCashOnDelivery$ = undefined;
        this.realTimeStockItemBalanceUpdate$ = undefined;
        this.soLockSettings$ = undefined;

        this.isEdara2IntegrationConnected$ = undefined;
        this.edaraSetupCompleted$ = undefined;
        this.globalSettings$ = undefined;
        this.organizationProfile$ = undefined;
        this.ordersSettings$ = undefined;
        this.purchaseInvoicesSettings$ = undefined;
        this.salesInvoicesSettings$ = undefined;
        this.productsSettings$ = undefined;
        this.subscriptionsSettings$ = undefined;
        this.subscriptionsChurnReasons$ = undefined;
        this.subscriptionsFxRates$ = undefined;
        this.subscriptionsCurrenciesUsed$ = undefined;
        this.subscriptionsOpeningBalances$ = undefined;
        this.subscriptionsCustomerFields$ = undefined;
        this.subscriptionsProductFields$ = undefined;
        this.subscriptionsSubscriptionFields$ = undefined;
        this.subscriptionsWorkflows$ = undefined;
    }

    //#region System Settings

    findSystemSettingsAsync(query: any): Promise<PagedList<SystemSetting[]> | undefined> {
        const queryString = new QueryStringHelper().toQueryString(query);
        return firstValueFrom(
            this.httpClient.get<PagedList<SystemSetting[]>>(this.baseUrl + 'api/systemsettings/find?' + queryString)
        ).catch(() => { return undefined; });
    }

    getSystemSettingByName(name: string): Observable<SystemSetting> {
        return this.httpClient.get<SystemSetting>(this.baseUrl + 'api/systemsettings/FindByName/' + name);
    }

    getSystemSettingByNameAync(name: string): Promise<SystemSetting | undefined> {
        return firstValueFrom(
            this.httpClient.get<SystemSetting>(this.baseUrl + 'api/systemsettings/FindByName/' + name)
        ).catch(() => { return undefined; });
    }

    getModulesBasedOnPricingPlan(): Promise<SystemSetting | undefined> {
        return firstValueFrom(
            this.httpClient.get<SystemSetting>(this.baseUrl + 'api/SystemSettings/GetModulesBasedOnPricingPlan')
        ).catch(() => { return undefined; });
    }

    saveSystemSetting(systemSetting: SystemSetting): Observable<SystemSetting> {
        return this.httpClient.put<SystemSetting>(this.baseUrl + 'api/systemsettings/SaveOrUpdate', systemSetting);
    }

    saveSystemSettingAsync(systemSetting: SystemSetting): Promise<SystemSetting | undefined> {
        return firstValueFrom(
            this.httpClient.put<SystemSetting>(this.baseUrl + 'api/systemsettings/SaveOrUpdate', systemSetting)
        ).catch(() => { return undefined; });
    }

    getSystemSettingByNameLocalAsync(name: string): Promise<SystemSetting> {
        return this.localDbProvider.db.systemSettings
            .where({ name: name })
            .first((element: SystemSetting) => {
                return element;
            });
    }

    addSystemSettingsToLocalDb(settings: SystemSetting[]) {
        settings.forEach(element => {
            this.saveSystemSettingLocal(element);
        });
    }

    saveSystemSettingLocal(systemSetting?: SystemSetting) {
        if (!this.dataSourceService.canCacheLocally) return;
        if (systemSetting) {
            this.localDbProvider.db.systemSettings.put(systemSetting)
                .catch((err: any) => this.localDbProvider.handleErrors(err));
        }
    }

    deleteSystemSettingAsync(id: string): Promise<boolean> {
        return firstValueFrom(
            this.httpClient.delete<boolean>(this.baseUrl + 'api/systemsettings/' + id)
        ).catch(() => { return false; });
    }

    deleteSystemSettingLocal(id: string) {
        if (id) {
            this.localDbProvider.db.systemSettings.where({ id: id }).delete()
                .catch((err: any) => this.localDbProvider.handleErrors(err));
        }
    }

    //#endregion

    connectZatca(otp: string): Observable<EInvoiceKsaConfiguration> {
        return this.httpClient.get<EInvoiceKsaConfiguration>(this.baseUrl + 'api/SystemSettings/GetKSAEInvoiceConfiguration', {
            params: { OTP: otp }
        });
    }
}
